import { useMemo, useState } from 'react';
import { IconBell, IconChartBar, IconDots, IconEye, IconGift, IconMail, IconMessageCircle, IconPencil, IconPlus, IconSend, IconClick, IconArrowBackUp, IconUsers } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { DataError, useList } from '@hana/data';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { useT } from '@hana/i18n';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { CAMPAIGN_STATUS_LABEL, GIFT_STATUS_LABEL, GIFT_TABS, campaignRates, giftsByTab, rfqProgress, type GiftTab } from './logic';
import type { Campaign, CampaignStatus, GiftStatus, MailTemplate, Rfq } from './types';
import { useWide } from './useWide';
import './marketingList.i18n';
import s from './marketing.module.css';

// Mint-Marketing (1536 / 1920): Chiến dịch · Mẫu email · Quà tặng (+ Hỏi giá đối tác qua "Thêm"). Cột phải 372 ở 1920: So sánh chiến dịch · Khách nóng.
// Chỉ đọc; nút ghi báo toast. Giá chào của đối tác (RFQ) chỉ hiện với `?money=view`; không có ngân sách quà.

type Tab = 'cp' | 'tpl' | 'gift' | 'rfq';
const ST_CLS: Record<CampaignStatus, string> = { DONE: s.ok!, SENDING: s.pri!, PAUSED: s.warn!, DRAFT: s.none!, SCHEDULED: s.pri! };
const GIFT_CLS: Record<GiftStatus, string> = { PROPOSED: s.warn!, APPROVED: s.pri!, ORDERED: s.pri!, SHIPPING: s.pri!, DELIVERED: s.ok!, REJECTED: s.errC!, FAILED: s.errC! };
const THUMB: Record<MailTemplate['kind'], string> = {
  'Báo giá': 'linear-gradient(135deg, var(--d-blue), var(--link))',
  'Dịch vụ': 'linear-gradient(135deg, var(--ok), var(--d-green))',
  'Thông báo': 'linear-gradient(135deg, var(--tx2), var(--d-gray))',
  'Chăm sóc': 'linear-gradient(135deg, var(--warn), var(--d-amber))',
};
const MF = { reply: 'var(--d-green)', click: 'var(--d-amber)', open: 'var(--d-blue)', none: 'var(--bd)', fail: 'var(--err)' } as const;
const LEGEND: readonly (readonly [string, string])[] = [['Trả lời', MF.reply], ['Bấm', MF.click], ['Mở', MF.open], ['Chưa mở', MF.none], ['Lỗi · bounce', MF.fail]];
const HOT: readonly (readonly [string, string, number, number])[] = [['THACO', 'mở 4 · bấm 2', 4, 2], ['GREENBUD', 'mở 3 · bấm 1', 3, 1], ['SAVITA', 'bấm CTA', 1, 1]];
const NOTIS: readonly (readonly [string, string, string, boolean])[] = [
  ['MKT-6UHT · 3 khách trả lời', 'Hôm qua 10:20', 'var(--link)', true],
  ['RFQ-261019-TRF · VTHAILONG đã báo giá', '20/Oct 09:10', 'var(--link)', true],
  ['Quà: 2 đề xuất chờ duyệt', '18/Oct 16:00', 'var(--d-amber)', false],
];
const CP_W = [520, 140, 170, 300, 134] as const;
const CP_H = ['Chiến dịch', 'Trạng thái', 'Ngày gửi', 'Gửi → mở → bấm → trả lời', 'Người tạo'] as const;
const GIFT_COLS: readonly (readonly [string, number])[] = [['Người nhận', 240], ['Nhóm', 90], ['Sale', 90], ['Quà', 190], ['Trạng thái', 130], ['Giao', 200]];

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}
const sendDate = (c: Campaign, start?: string) => (c.status === 'DRAFT' ? '—' : c.status === 'SCHEDULED' ? `Hẹn ${start ?? c.date}` : (start ?? c.date));
const isZalo = (c: Campaign) => c.id.includes('ZL');
const hasMail = (c: Campaign) => !isZalo(c);
const hasZalo = (c: Campaign) => isZalo(c) || c.id === 'MKT-7ODM';
const initials = (n: string) => n.slice(0, 2).toUpperCase();

function CampaignTable({ rows, starts, onOpen }: { rows: readonly Campaign[]; starts: Record<string, string>; onOpen: (c: Campaign) => void }) {
  return (
    <section className={s.card} aria-label="Chiến dịch">
      <table className={s.table} aria-label="Chiến dịch">
        <colgroup>{CP_W.map((w, i) => <col key={i} style={{ width: i === 0 ? 'auto' : w }} />)}</colgroup>
        <thead><tr>{CP_H.map((h) => <th key={h} scope="col">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((c) => {
            const r = campaignRates(c);
            const seg: readonly (readonly [number, string])[] = [[c.reply, MF.reply], [c.click - c.reply, MF.click], [c.open - c.click, MF.open], [c.sent - c.open, MF.none], [c.fail, MF.fail]];
            return (
              <tr key={c.id} onClick={() => onOpen(c)}>
                <td title={`${c.id} · ${c.filter}`}>
                  {hasMail(c) && <span className={s.ch} role="img" aria-label="Email" title="Email" style={{ color: 'var(--tx2)' }}><IconMail size={15} aria-hidden="true" /></span>}
                  {hasZalo(c) && <span className={s.ch} role="img" aria-label="Zalo OA · Broadcast" title="Zalo OA · Broadcast" style={{ color: 'var(--link)' }}><IconMessageCircle size={15} aria-hidden="true" /></span>}
                  <b className={s.b}>{c.subject}</b>
                </td>
                <td><span className={`${s.st} ${ST_CLS[c.status]}`}><span className={s.stDot} />{CAMPAIGN_STATUS_LABEL[c.status]}</span></td>
                <td className={s.tx2}>{sendDate(c, starts[c.id])}</td>
                <td>
                  {c.sent > 0 && (
                    <div className={s.mf} role="img" aria-label={`${c.id}: gửi ${c.sent}, mở ${c.open}, bấm ${c.click}, trả lời ${c.reply}, lỗi ${c.fail}`}>
                      {seg.map(([f, col], i) => f > 0 && <div key={i} style={{ flex: f, background: col }} />)}
                    </div>
                  )}
                  <div className={s.mfNums}>
                    {c.sent > 0 ? (
                      <>
                        <span title="Đã gửi"><IconSend size={13} aria-hidden="true" />{c.sent}</span>
                        <span title="Tỷ lệ mở" style={{ color: 'inherit' }}><IconEye size={13} aria-hidden="true" style={{ color: 'var(--d-blue)' }} />{r.open}%</span>
                        <span title="Tỷ lệ bấm"><IconClick size={13} aria-hidden="true" style={{ color: 'var(--d-amber)' }} />{r.click}%</span>
                        <span title="Trả lời"><IconArrowBackUp size={13} aria-hidden="true" style={{ color: 'var(--ok)' }} />{c.reply}</span>
                      </>
                    ) : (
                      <span title="Chưa gửi · số khách khớp tệp"><IconUsers size={13} aria-hidden="true" />{c.total || '—'}</span>
                    )}
                  </div>
                </td>
                <td title={`Tạo ${c.date} · ${c.owner}`}><span className={s.owner}><span className={s.ab} aria-hidden="true">{initials(c.owner)}</span>{c.owner}</span></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className={s.legend}>{LEGEND.map(([k, col]) => <span key={k}><span className={s.sq} style={{ background: col }} />{k}</span>)}</div>
    </section>
  );
}

function Templates({ rows, toast }: { rows: readonly MailTemplate[]; toast: (m: string) => void }) {
  const tx = useT();
  return (
    <div className={s.tplGrid}>
      {rows.map((t) => (
        <section key={t.id} className={`${s.tpl} ${t.active ? '' : s.tplOff}`} aria-label={`Mẫu ${t.id}`}>
          <div className={s.thumb} style={{ background: THUMB[t.kind] }}><span className={s.kind}>{t.kind}</span></div>
          <div className={s.tplBody}>
            <b>{t.name}</b>
            <div className={s.sub}>{t.id} · VI · {t.used}</div>
            <div className={s.tplAct}>
              <button type="button" className={s.act} onClick={() => toast(tx('mk.previewSoon'))}><IconEye size={16} aria-hidden="true" />{tx('mk.preview')}</button>
              <button type="button" className={s.act} aria-label="Sửa mẫu" title="Sửa mẫu" onClick={() => toast('Chưa mở ghi ở bản này')}><IconPencil size={16} aria-hidden="true" /></button>
              <button type="button" className={s.act} onClick={() => toast('Chưa mở ghi ở bản này')}>{t.active ? 'Tắt' : 'Bật lại'}</button>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}

function RfqPane({ rows, money }: { rows: readonly Rfq[]; money: boolean }) {
  const [i, setI] = useState(0);
  const cur = rows[Math.min(i, rows.length - 1)];
  if (!cur) return null;
  return (
    <div className={s.rfq}>
      <section className={s.card} aria-label="Hỏi giá đối tác">
        <div className={s.rfqList}>
          {rows.map((r, k) => {
            const p = rfqProgress(r);
            return (
              <button key={r.id} type="button" className={s.rfqItem} aria-pressed={k === i} onClick={() => setI(k)}>
                <b className={s.b}>{r.id}</b>
                <span>{r.subject}</span>
                <span className={s.sub}>{r.replies.length} đối tác · gắn {r.deal}</span>
                <span className={s.sub} style={{ color: p.answered < p.total ? 'var(--warn)' : 'var(--ok)' }}>{p.answered} / {p.total} đã trả lời</span>
              </button>
            );
          })}
        </div>
      </section>
      <section className={s.card} aria-label={`Trả lời ${cur.id}`}>
        <div className={s.rfqHead}><h2>{cur.subject}</h2><div className={s.sub}>{cur.scope}</div></div>
        {cur.replies.map((p) => (
          <div key={p.partner} className={s.rep}>
            <div><b className={s.b}>{p.partner}</b><div className={s.sub}>{p.type}</div></div>
            <span style={{ color: p.replied ? 'var(--ok)' : 'var(--warn)' }}>{p.answered}</span>
            <span className={s.tx2}>{p.lead}</span>
            <span>{money ? p.price : ''}</span>
          </div>
        ))}
      </section>
    </div>
  );
}

function Dock({ campaigns, onClose }: { campaigns: readonly Campaign[]; onClose: () => void }) {
  const tx = useT();
  const [tab, setTab] = useState<'rep' | 'noti'>('rep');
  const cmp = campaigns.filter((c) => c.total > 0);
  return (
    <div className={s.dock}>
      <div className={s.dkBar}>
        <div className={s.dkTabs} role="tablist" aria-label="Cột phải">
          {([['rep', 'Báo cáo'], ['noti', 'Thông báo']] as const).map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>{l}</button>)}
        </div>
        <button type="button" className={s.dkClose} aria-label={tx('mk.closeDock')} onClick={onClose}>×</button>
      </div>
      {tab === 'rep' ? (
        <>
          <section className={s.dkCard}>
            <h2>{tx('mk.cmp')}</h2>
            <div className={s.dkLeg}><span><span className={s.dot} style={{ background: MF.open }} />{tx('mk.open')}</span><span><span className={s.dot} style={{ background: MF.click }} />{tx('mk.click')}</span></div>
            {cmp.map((c) => {
              const r = campaignRates(c);
              return (
                <div key={c.id} className={s.cmp} role="img" aria-label={`${c.id}: gửi ${c.sent}, mở ${r.open}%, bấm ${r.click}%, trả lời ${c.reply}`}>
                  <div className={s.cmpTop}><b className={s.b}>{c.id}</b><span className={s.sub}>{c.date}</span></div>
                  <div className={s.bar}><div className={s.track}><div className={s.fill} style={{ width: `${r.open}%`, background: MF.open }} /></div><span className={s.barV}>{r.open}%</span></div>
                  <div className={s.bar}><div className={s.track}><div className={s.fill} style={{ width: `${r.click}%`, background: MF.click }} /></div><span className={s.barV}>{r.click}%</span></div>
                  <div className={s.sub} style={{ marginTop: 4 }}>Gửi {c.sent} · lỗi {((c.fail / c.total) * 100).toFixed(1).replace('.', ',')}% · trả lời {c.reply}</div>
                </div>
              );
            })}
          </section>
          <section className={s.dkCard}>
            <h2>{tx('mk.hot')}</h2>
            <div role="img" aria-label="Khách nóng: THACO mở 4 bấm 2, GREENBUD mở 3 bấm 1, SAVITA bấm CTA">
              {HOT.map(([k, v, o, c]) => (
                <div key={k} className={s.hot}>
                  <span>{k}</span>
                  <div className={s.hotBar}><div style={{ flex: o, background: MF.open }} /><div style={{ flex: c, background: MF.click }} /><div style={{ flex: 6 - o - c }} /></div>
                  <span className={s.hotV}>{v}</span>
                </div>
              ))}
            </div>
            <div className={s.sub} style={{ marginTop: 6 }}>{tx('mk.hotNote')}</div>
          </section>
        </>
      ) : (
        <div>
          {NOTIS.map(([k, when, col, unread]) => (
            <button key={k} type="button" className={s.noti}>
              <span className={s.nDot} style={{ background: col }} />
              <span><span style={{ display: 'block', fontWeight: unread ? 600 : 450 }}>{k}</span><span className={s.sub} style={{ display: 'block', marginTop: 2 }}>{when}</span></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function MarketingList() {
  const tx = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const camps = useList('mktCampaign', { limit: 200 });
  const dets = useList('mktCampaignDetail', { limit: 200 });
  const tpls = useList('mktTemplate', { limit: 200 });
  const gifts = useList('mktGift', { limit: 200 });
  const rfqs = useList('mktRfq', { limit: 50 });
  const forced = readState();
  const params = new URLSearchParams(location.search);
  const money = params.get('money') === 'view';
  const initTab = params.get('tab');
  const [tab, setTab] = useState<Tab>(initTab === 'tpl' || initTab === 'gift' || initTab === 'rfq' ? initTab : 'cp');
  const [gTab, setGTab] = useState<GiftTab>('all');
  const [moreOpen, setMoreOpen] = useState(false);
  const wide = useWide();
  const [dockOpen, setDockOpen] = useState(true);

  const cpAll = camps.data?.items ?? [];
  const starts = Object.fromEntries((dets.data?.items ?? []).map((d) => [d.campaign, d.start]));
  const tplAll = tpls.data?.items ?? [];
  const giftAll = gifts.data?.items ?? [];
  const rfqAll = rfqs.data?.items ?? [];
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const soon = (what: string) => () => toast({ msg: `${what} chưa mở ở bản này` });

  const first = camps;
  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (first.isLoading) state = 'loading';
  else if (first.isError) state = first.error instanceof DataError && first.error.code === 'noperm' ? 'noperm' : 'error';
  else if (cpAll.length + tplAll.length + giftAll.length === 0) state = 'empty';
  const ready = state === 'ready';

  const giftRows = useMemo(() => giftsByTab(giftAll, gTab), [giftAll, gTab]);
  const items = [
    { key: 'cp', label: 'Chiến dịch', n: ready ? cpAll.length : undefined },
    { key: 'tpl', label: 'Mẫu email', n: ready ? 18 : undefined },
    { key: 'gift', label: 'Quà tặng', n: ready ? giftAll.length : undefined },
  ];
  const inMore = tab === 'rfq';
  const PRIMARY: Record<Tab, string> = { cp: 'Tạo chiến dịch', tpl: 'Mẫu mới', gift: 'Tạo đợt quà', rfq: 'Hỏi giá đối tác' };

  const body = () => {
    if (tab === 'cp') return <CampaignTable rows={cpAll} starts={starts} onOpen={(c) => void navigate({ to: '/ezy/marketing/$campaignId', params: { campaignId: c.id } })} />;
    if (tab === 'tpl') return <Templates rows={tplAll} toast={(m) => toast({ msg: m })} />;
    if (tab === 'rfq') return <RfqPane rows={rfqAll} money={money} />;
    return (
      <>
        <div className={s.chipRow}>
          {(Object.keys(GIFT_TABS) as GiftTab[]).map((k) => (
            <button key={k} type="button" className={s.chipTab} aria-pressed={gTab === k} onClick={() => setGTab(k)}>{GIFT_TABS[k].label}</button>
          ))}
          <div className={s.kpis}><span>{tx('mk.recv')} <b>{giftAll.length}</b></span><span>{tx('mk.delivered')} <b>{giftsByTab(giftAll, 'done').length}</b></span></div>
        </div>
        <section className={s.card} aria-label="Quà tặng">
          <table className={`${s.table} ${s.gift}`} aria-label="Quà tặng">
            <colgroup>{GIFT_COLS.map(([k, w]) => <col key={k} style={{ width: w }} />)}</colgroup>
            <thead><tr>{GIFT_COLS.map(([k]) => <th key={k} scope="col">{k}</th>)}</tr></thead>
            <tbody>
              {giftRows.map((g) => (
                <tr key={g.id}>
                  <td><b className={s.b}>{g.customer}</b><div className={s.sub}>{g.recipient}</div></td>
                  <td className={s.tx2}>{g.group}</td>
                  <td>{g.sale}</td>
                  <td>{g.gift}</td>
                  <td><span className={`${s.st} ${GIFT_CLS[g.status]}`}>{GIFT_STATUS_LABEL[g.status]}</span></td>
                  <td className={s.tx2}>{g.ship}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </>
    );
  };

  return (
    <Page kind="table" dock={ready && wide && dockOpen ? <Dock campaigns={cpAll} onClose={() => setDockOpen(false)} /> : undefined}>
      <PageHeader
        title="Marketing"
        tabs={
          <div className={s.tabs}>
            <Segmented label="Marketing" value={inMore ? '' : tab} onChange={(k) => { setMoreOpen(false); setTab(k as Tab); }} items={items} />
            <button type="button" className={`${s.more} ${inMore ? s.moreOn : ''}`} aria-haspopup="menu" aria-expanded={moreOpen} onClick={() => setMoreOpen((o) => !o)}>
              <IconPlus size={16} aria-hidden="true" />
              {inMore ? 'Hỏi giá' : 'Thêm'} ▾
            </button>
            {moreOpen && (
              <div className={s.pop} role="menu" aria-label="Thêm">
                <button type="button" role="menuitem" className={s.popItem} onClick={() => { setTab('rfq'); setMoreOpen(false); }}>{tx('mk.rfq')}<span className={s.sub}>{rfqAll.length}</span></button>
              </div>
            )}
          </div>
        }
        actions={
          <>
            <HeaderTool label="Mở cột phải" icon={IconChartBar} active={wide && dockOpen} onClick={wide ? () => setDockOpen((o) => !o) : soon('Báo cáo')} />
            <HeaderTool label="Thông báo" icon={IconBell} n={2} />
            <HeaderTool label="Thao tác khác" icon={IconDots} onClick={soon('Menu thao tác')} />
            <Button variant="primary" icon={tab === 'gift' ? <IconGift size={16} aria-hidden="true" /> : <IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>{PRIMARY[tab]}</Button>
          </>
        }
      />
      <div className={s.page}>
          <StateView
            state={state}
            title={state === 'empty' ? 'Chưa có chiến dịch' : undefined}
            icon={state === 'empty' ? <IconMail size={26} stroke={1.8} /> : undefined}
            action={state === 'error' ? <Button onClick={() => void camps.refetch()}>{tx('mk.retry')}</Button> : undefined}
            skeleton={<Skeleton rows={6} />}
          >
            {body()}
          </StateView>
      </div>
    </Page>
  );
}
